// frontend/src/modules/reports/pages/financial/InvoiceAgingPage.tsx
//
// Phase 4 (Reports Frontend), Task 4. Point-in-time report (no from/to) —
// uses ReportShell's `noDateRange` mode. Per-currency sections; each
// currency's table ends with the backend-computed TOTAL row
// (`report.totals`, client_id/client_name null) appended after the
// per-client rows — never re-summed client-side.

import * as React from 'react';
import { ReportShell } from '../../components/ReportShell';
import { DataTable } from '../../../../design-system/components/DataTable';
import { VISUAL } from '../../../../design-system/visual';
import { formatMoney, formatDate } from '../../../../design-system/format';
import { reportsApi, downloadReportCsv } from '../../api';
import type { AgingReport, AgingRow } from '../../types';
import { useReportFetch } from './useReportFetch';
import { groupByCurrency } from './currencyGroup';

export function InvoiceAgingPage() {
  const fetcher = React.useCallback(() => reportsApi.invoiceAging({}), []);
  const result = useReportFetch<AgingReport>(fetcher, []);
  const forbidden = result.status === 403;

  const report = result.data;
  const rows = report?.rows ?? [];
  const totals = report?.totals ?? [];
  const totalsByCurrency = new Map(totals.map((t) => [t.currency, t]));
  const sections = groupByCurrency(rows);

  return (
    <ReportShell
      eyebrow="Reports / Financial"
      title="Invoice aging"
      description={report ? `As of ${formatDate(report.as_of)}` : 'Outstanding invoice balances by age bucket.'}
      noDateRange
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && result.loading}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={!forbidden && !result.loading && !result.error && rows.length === 0}
      csv={{
        filename: `invoice-aging-${report?.as_of ?? 'now'}.csv`,
        onDownload: () => downloadReportCsv('invoice-aging', {}, `invoice-aging-${report?.as_of ?? 'now'}.csv`),
      }}
    >
      {report && (
        <div className="space-y-6">
          {sections.map(([currency, clientRows]) => {
            const total = totalsByCurrency.get(currency);
            const tableRows: AgingRow[] = total ? [...clientRows, total] : clientRows;
            return (
              <div key={currency} className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                <DataTable
                  rows={tableRows}
                  rowKey={(r) => r.client_id ?? `total-${currency}`}
                  columns={[
                    {
                      key: 'client',
                      header: `Client (${currency})`,
                      cell: (r) => (
                        <span className={r.client_id === null ? 'font-bold text-foreground' : 'font-medium text-foreground'}>
                          {r.client_id === null ? 'TOTAL' : r.client_name}
                        </span>
                      ),
                    },
                    { key: 'current', header: 'Current', cell: (r) => formatMoney(r.current, currency) },
                    { key: 'd1_30', header: '1-30 days', cell: (r) => formatMoney(r.d1_30, currency) },
                    { key: 'd31_60', header: '31-60 days', cell: (r) => formatMoney(r.d31_60, currency) },
                    { key: 'd61_90', header: '61-90 days', cell: (r) => formatMoney(r.d61_90, currency) },
                    { key: 'd90_plus', header: '90+ days', cell: (r) => formatMoney(r.d90_plus, currency) },
                    {
                      key: 'total',
                      header: 'Outstanding',
                      cell: (r) => <span className={r.client_id === null ? 'font-bold' : ''}>{formatMoney(r.outstanding_total, currency)}</span>,
                    },
                  ]}
                />
              </div>
            );
          })}
        </div>
      )}
    </ReportShell>
  );
}
